import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Badge, Button, Form, Alert, Spinner, Modal } from 'react-bootstrap';
import { getMonthlySummary, previewInvoice, generateInvoice, sendInvoice, getInvoices } from './DataService';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import './OrdersToBill.css';

const OrdersToBill = () => {
  const [summary, setSummary] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // KPIs
  const [kpis, setKpis] = useState({
    totalAFacturer: 0,
    paiementEnAttente: 0,
    dejaPaye: 0,
    totalMois: 0
  });

  // Calculer le mois précédent par défaut
  const getPreviousMonth = () => {
    const now = new Date();
    const prevMonth = now.getMonth(); // getMonth() retourne 0-11, donc le mois actuel - 1 = mois précédent
    const prevYear = prevMonth === 0 ? now.getFullYear() - 1 : now.getFullYear();
    const month = prevMonth === 0 ? 12 : prevMonth;
    return { year: prevYear, month };
  };

  const previousMonth = getPreviousMonth();

  // Filtres
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(previousMonth.year);
  const [selectedMonth, setSelectedMonth] = useState(previousMonth.month);
  const [selectedClientType, setSelectedClientType] = useState(null);

  // Aperçu de la facture avant émission : { url, manutentionsAConfirmer }
  const [apercu, setApercu] = useState(null);
  const [apercuEnCours, setApercuEnCours] = useState(false);

  // Modal pour générer une facture
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [selectedSummaryItem, setSelectedSummaryItem] = useState(null);
  const [generating, setGenerating] = useState(false);

  // Modal pour envoyer une facture
  const [showSendModal, setShowSendModal] = useState(false);
  const [generatedInvoice, setGeneratedInvoice] = useState(null);
  const [customMessage, setCustomMessage] = useState('');
  const [sending, setSending] = useState(false);

  const monthNames = [
    'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
  ];

  useEffect(() => {
    fetchSummary();
  }, [selectedYear, selectedMonth, selectedClientType]);

  const fetchSummary = async () => {
    setLoading(true);
    setError(null);
    try {
      // Récupérer les commandes à facturer
      const data = await getMonthlySummary(selectedYear, selectedMonth, selectedClientType);
      setSummary(data.summary || []);

      // Calculer le total à facturer
      const totalAFacturer = (data.summary || []).reduce((sum, item) => sum + item.total_ttc, 0);

      // Récupérer les factures du mois pour calculer les KPIs
      let paiementEnAttente = 0;
      let dejaPaye = 0;

      if (selectedMonth) {
        const invoicesData = await getInvoices({
          year: selectedYear,
          month: selectedMonth,
          nbpage: 1000 // Récupérer toutes les factures du mois
        });

        if (invoicesData.success && invoicesData.data) {
          invoicesData.data.forEach(invoice => {
            if (invoice.statu_facture === 1) {
              paiementEnAttente += invoice.total_ttc;
            } else if (invoice.statu_facture === 2) {
              dejaPaye += invoice.total_ttc;
            }
          });
        }
      }

      const totalMois = totalAFacturer + paiementEnAttente + dejaPaye;

      setKpis({
        totalAFacturer,
        paiementEnAttente,
        dejaPaye,
        totalMois
      });

    } catch (err) {
      setError('Erreur lors de la récupération des données');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateInvoice = (summaryItem) => {
    setSelectedSummaryItem(summaryItem);
    fermerApercu();
    setShowGenerateModal(true);
  };

  // L'URL d'un blob vit jusqu'à révocation explicite : sans ça, chaque aperçu
  // laisserait un PDF en mémoire pour la durée de la session.
  const fermerApercu = () => {
    setApercu((precedent) => {
      if (precedent?.url) {
        window.URL.revokeObjectURL(precedent.url);
      }
      return null;
    });
  };

  const voirApercu = async () => {
    if (!selectedSummaryItem) return;

    setApercuEnCours(true);
    setError(null);
    try {
      fermerApercu();
      setApercu(
        await previewInvoice(
          selectedSummaryItem.client_id,
          selectedSummaryItem.year,
          selectedSummaryItem.month
        )
      );
    } catch (err) {
      // La réponse d'erreur est un blob puisqu'on a demandé du binaire :
      // l'afficher tel quel donnerait « [object Blob] ».
      let message = 'Erreur lors de la génération de l\'aperçu';
      try {
        message = JSON.parse(await err.response?.data?.text())?.error || message;
      } catch (_) {
        // Réponse illisible : on garde le message générique.
      }
      setError(message);
      console.error(err);
    } finally {
      setApercuEnCours(false);
    }
  };

  const confirmGenerateInvoice = async () => {
    if (!selectedSummaryItem) return;

    setGenerating(true);
    setError(null);
    try {
      const result = await generateInvoice(
        selectedSummaryItem.client_id,
        selectedSummaryItem.year,
        selectedSummaryItem.month
      );

      if (result.success) {
        setSuccess(`Facture ${result.num_facture} générée avec succès !`);
        setGeneratedInvoice({
          ...result,
          client_id: selectedSummaryItem.client_id,
          client_email: selectedSummaryItem.client_email
        });
        fermerApercu();
        setShowGenerateModal(false);
        setShowSendModal(true);
        fetchSummary(); // Rafraîchir la liste
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la génération de la facture');
      console.error(err);
    } finally {
      setGenerating(false);
    }
  };

  const handleSendInvoice = async () => {
    if (!generatedInvoice) return;

    setSending(true);
    setError(null);
    try {
      const result = await sendInvoice(
        generatedInvoice.num_facture,
        generatedInvoice.client_id,
        customMessage || null
      );

      if (result.success) {
        setSuccess(`Facture envoyée avec succès à ${result.email}`);
        setShowSendModal(false);
        setCustomMessage('');
        setGeneratedInvoice(null);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de l\'envoi de la facture');
      console.error(err);
    } finally {
      setSending(false);
    }
  };

  const getClientTypeBadge = (clientType) => {
    const badges = {
      fournisseur: { bg: 'primary', icon: '🏢', label: 'Fournisseur' },
      particulier: { bg: 'success', icon: '👤', label: 'Particulier' },
      entreprise: { bg: 'warning', icon: '💼', label: 'Entreprise' }
    };
    const badge = badges[clientType] || { bg: 'secondary', icon: '❓', label: 'Inconnu' };
    return (
      <Badge bg={badge.bg} className={badge.bg === 'warning' ? 'text-dark' : ''}>
        {badge.icon} {badge.label}
      </Badge>
    );
  };

  return (
    <Container fluid className="orders-to-bill-container py-4">
      <Row className="mb-4">
        <Col>
          <h2>Commandes à facturer</h2>
          <p className="text-muted">Gérez les commandes manuelles à facturer par client et par mois</p>
        </Col>
      </Row>

      {/* Filtres */}
      <Card className="mb-4">
        <Card.Body>
          <Row className="align-items-end">
            <Col md={3}>
              <Form.Group>
                <Form.Label>Année</Form.Label>
                <Form.Select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                >
                  {[currentYear, currentYear - 1, currentYear - 2].map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Mois</Form.Label>
                <Form.Select
                  value={selectedMonth || ''}
                  onChange={(e) => setSelectedMonth(e.target.value ? parseInt(e.target.value) : null)}
                >
                  <option value="">Tous les mois</option>
                  {monthNames.map((month, index) => (
                    <option key={index + 1} value={index + 1}>{month}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Type de client</Form.Label>
                <Form.Select
                  value={selectedClientType || ''}
                  onChange={(e) => setSelectedClientType(e.target.value || null)}
                >
                  <option value="">Tous</option>
                  <option value="fournisseur">Fournisseur</option>
                  <option value="particulier">Particulier</option>
                  <option value="entreprise">Entreprise</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Button variant="primary" onClick={fetchSummary} disabled={loading}>
                {loading ? <Spinner animation="border" size="sm" /> : 'Actualiser'}
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* KPIs */}
      {selectedMonth && (
        <Row className="mb-4">
          <Col md={3}>
            <Card className="h-100 border-primary">
              <Card.Body className="text-center">
                <h6 className="text-muted mb-2">À facturer</h6>
                <h3 className="text-primary mb-0">{kpis.totalAFacturer.toFixed(2)} €</h3>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="h-100 border-warning">
              <Card.Body className="text-center">
                <h6 className="text-muted mb-2">Paiement en attente</h6>
                <h3 className="text-warning mb-0">{kpis.paiementEnAttente.toFixed(2)} €</h3>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="h-100 border-success">
              <Card.Body className="text-center">
                <h6 className="text-muted mb-2">Déjà payé</h6>
                <h3 className="text-success mb-0">{kpis.dejaPaye.toFixed(2)} €</h3>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="h-100 border-dark">
              <Card.Body className="text-center">
                <h6 className="text-muted mb-2">Total du mois</h6>
                <h3 className="text-dark mb-0">{kpis.totalMois.toFixed(2)} €</h3>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* Messages */}
      {error && <Alert variant="danger" onClose={() => setError(null)} dismissible>{error}</Alert>}
      {success && <Alert variant="success" onClose={() => setSuccess(null)} dismissible>{success}</Alert>}

      {/* Liste des commandes à facturer */}
      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Chargement...</span>
          </Spinner>
        </div>
      ) : summary.length === 0 ? (
        <Alert variant="info">
          Aucune commande à facturer pour les critères sélectionnés
        </Alert>
      ) : (
        <Row>
          {summary.map((item, index) => (
            <Col key={index} md={6} lg={4} className="mb-4">
              <Card className="h-100 shadow-sm">
                <Card.Header className="bg-primary text-white">
                  <div className="d-flex justify-content-between align-items-center">
                    <strong>{item.client_name}</strong>
                    {getClientTypeBadge(item.client_type)}
                  </div>
                  <small>{item.client_email}</small>
                </Card.Header>
                <Card.Body>
                  <div className="mb-3">
                    <h5 className="text-muted mb-1">
                      {monthNames[item.month - 1]} {item.year}
                    </h5>
                    <div className="d-flex justify-content-between align-items-center">
                      <span>{item.unfactured_orders_count} commande{item.unfactured_orders_count > 1 ? 's' : ''}</span>
                      <h4 className="text-primary mb-0">{item.total_ttc.toFixed(2)} €</h4>
                    </div>
                  </div>

                  {/* Liste des commandes */}
                  {item.orders && item.orders.length > 0 && (
                    <div className="orders-list mb-3">
                      <small className="text-muted">Détails des commandes :</small>
                      <div className="mt-2" style={{ maxHeight: '150px', overflowY: 'auto' }}>
                        {item.orders.map((order, idx) => (
                          <div key={idx} className="border-bottom pb-1 mb-1">
                            <small>
                              <strong>#{order.id}</strong> - {format(new Date(order.DateCommande), 'dd/MM/yyyy')} - {order.TTC.toFixed(2)} €
                              {order.promo_code && (
                                <span style={{ color: '#e67e00', marginLeft: 6 }}>
                                  🏷 {order.promo_code} (-{order.promo_remise.toFixed(2)} €)
                                </span>
                              )}
                            </small>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <Button
                    variant="success"
                    className="w-100"
                    onClick={() => handleGenerateInvoice(item)}
                  >
                    Générer la facture
                  </Button>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      )}

      {/* Modal de confirmation de génération */}
      <Modal size="lg" show={showGenerateModal} onHide={() => { fermerApercu(); setShowGenerateModal(false); }}>
        <Modal.Header closeButton>
          <Modal.Title>Générer une facture</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedSummaryItem && (
            <>
              <p>
                Vous êtes sur le point de générer une facture mensuelle pour :
              </p>
              <ul>
                <li><strong>Client :</strong> {selectedSummaryItem.client_name}</li>
                <li><strong>Email :</strong> {selectedSummaryItem.client_email}</li>
                <li><strong>Période :</strong> {monthNames[selectedSummaryItem.month - 1]} {selectedSummaryItem.year}</li>
                <li><strong>Nombre de commandes :</strong> {selectedSummaryItem.unfactured_orders_count}</li>
                <li><strong>Montant total TTC :</strong> {selectedSummaryItem.total_ttc.toFixed(2)} €</li>
              </ul>
              <p className="text-warning">
                ⚠️ Cette action marquera les commandes comme facturées, et seule une
                commande en ligne de commande permet de revenir en arrière.
              </p>

              {apercu?.manutentionsAConfirmer > 0 && (
                <Alert variant="warning" className="py-2">
                  {apercu.manutentionsAConfirmer} manutention(s) non chiffrée(s) :
                  elles seront facturées 0 €, sans retour possible.
                </Alert>
              )}

              {apercu && (
                <iframe
                  title="Aperçu de la facture"
                  src={apercu.url}
                  style={{ width: '100%', height: '60vh', border: '1px solid #dee2e6' }}
                />
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => { fermerApercu(); setShowGenerateModal(false); }} disabled={generating}>
            Annuler
          </Button>
          {/* Le document avant l'engagement : le récapitulatif ci-dessus ne dit
              rien d'une adresse de facturation absente ou d'une commande de trop. */}
          <Button variant="outline-primary" onClick={voirApercu} disabled={generating || apercuEnCours}>
            {apercuEnCours ? <Spinner animation="border" size="sm" /> : (apercu ? 'Actualiser l\'aperçu' : 'Voir l\'aperçu')}
          </Button>
          <Button variant="primary" onClick={confirmGenerateInvoice} disabled={generating}>
            {generating ? <Spinner animation="border" size="sm" /> : 'Confirmer'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal d'envoi de facture */}
      <Modal show={showSendModal} onHide={() => setShowSendModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Envoyer la facture par email</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {generatedInvoice && (
            <>
              <Alert variant="success">
                Facture <strong>{generatedInvoice.num_facture}</strong> générée avec succès !
              </Alert>
              <p>
                Voulez-vous l'envoyer maintenant par email à <strong>{generatedInvoice.client_email}</strong> ?
              </p>
              <Form.Group className="mb-3">
                <Form.Label>Message personnalisé (optionnel)</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={customMessage}
                  onChange={(e) => setCustomMessage(e.target.value)}
                  placeholder="Ajoutez un message personnalisé à l'email..."
                  maxLength={1000}
                />
              </Form.Group>
              {generatedInvoice.pdf_url && (
                <div className="mb-3">
                  <a
                    href={generatedInvoice.pdf_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-outline-primary btn-sm"
                  >
                    Télécharger le PDF
                  </a>
                </div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowSendModal(false)} disabled={sending}>
            Plus tard
          </Button>
          <Button variant="primary" onClick={handleSendInvoice} disabled={sending}>
            {sending ? <Spinner animation="border" size="sm" /> : 'Envoyer maintenant'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default OrdersToBill;
